// Right-side slide-over drawer for the contract Performance tile. Hosts
// three tabs: 'History' (default — a step-line chart of past grade
// snapshots over a user-selected window), 'How is this calculated?' (the
// original derivation explainer) and 'Issue evidence' (the contextual
// rollup from the Issues register — severity counts, time-to-resolve,
// time-in-awaiting, overdue-action ratio + standalone-issue callout).
// Issues are evidence-only — they do NOT alter the A–E letter grade.
// Each panel's data hook only fires when its tab is active so dormant
// tabs cost nothing.

import {
  useRef,
  useState,
  lazy,
  Suspense,
  type JSX,
  type KeyboardEvent,
} from "react";
import SlideOverDrawer from "../../common/SlideOverDrawer";
import TabButton from "../../common/TabButton";
import LoadingSpinner from "../../common/LoadingSpinner";

// Lazy-loaded so recharts ships as an on-demand chunk fetched only when the
// performance-history drawer tab is opened, not in the eager bundle.
const PerformanceHistoryChart = lazy(() => import("./PerformanceHistoryChart"));
import IssueEvidencePanel from "./IssueEvidencePanel";
import type { GradeResult } from "../../../lib/contract-overview/contractGrade";

interface PillarSummary {
  label: string;
  /** Score 0–100, or null when the pillar is awaiting data. */
  score: number | null;
  /** Sample size — `n=N` annotation in the drawer. */
  sampleSize: number;
}

export interface PerformanceGradeDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  result: GradeResult;
  /** Pillar breakdown rendered in the "Pillar contributions" section. */
  pillars: PillarSummary[];
  /** Per-contract watch threshold; pillars below count as critical. */
  watchThreshold: number;
  /** Per-contract success threshold; pillars at/above hit the success
   *  band on their dial. Surfaced for transparency. */
  successThreshold: number;
  /** Managed-contract id — drives the history hook in the History tab. */
  contractId: string;
  /** Org-level grade-band offset; passed straight through to the
   *  history chart so the A/B/C/D reference lines match the current
   *  configuration. Null when no offset is set. */
  gradeThresholdOffset: number | null;
}

type TabKey = "how" | "history" | "issue-evidence";

function formatPillarValue(p: PillarSummary): string {
  if (p.score === null) return "—";
  return `${p.score}%`;
}

function pillarStatus(p: PillarSummary, watch: number): string {
  if (p.score === null) return "Awaiting data";
  if (p.score < watch) return "Critical";
  if (p.score >= 90) return "On track";
  return "Watch";
}

const PANEL_HOW = "performance-drawer-panel-how";
const PANEL_HISTORY = "performance-drawer-panel-history";
const PANEL_EVIDENCE = "performance-drawer-panel-issue-evidence";

export default function PerformanceGradeDrawer({
  isOpen,
  onClose,
  result,
  pillars,
  watchThreshold,
  successThreshold,
  contractId,
  gradeThresholdOffset,
}: PerformanceGradeDrawerProps): JSX.Element {
  const [activeTab, setActiveTab] = useState<TabKey>("history");
  const howRef = useRef<HTMLButtonElement>(null);
  const historyRef = useRef<HTMLButtonElement>(null);
  const evidenceRef = useRef<HTMLButtonElement>(null);

  const onTabsKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const order: TabKey[] = ["history", "how", "issue-evidence"];
    const currentIdx = order.indexOf(activeTab);
    let nextIdx: number | null = null;
    if (event.key === "ArrowRight") nextIdx = (currentIdx + 1) % order.length;
    else if (event.key === "ArrowLeft")
      nextIdx = (currentIdx - 1 + order.length) % order.length;
    else if (event.key === "Home") nextIdx = 0;
    else if (event.key === "End") nextIdx = order.length - 1;
    if (nextIdx === null) return;
    event.preventDefault();
    const nextKey = order[nextIdx]!;
    setActiveTab(nextKey);
    const refByKey: Record<TabKey, React.RefObject<HTMLButtonElement | null>> = {
      history: historyRef,
      how: howRef,
      "issue-evidence": evidenceRef,
    };
    refByKey[nextKey].current?.focus();
  };

  return (
    <SlideOverDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Performance grade"
      subtitle={
        activeTab === "how"
          ? "How the grade is derived"
          : activeTab === "issue-evidence"
            ? "Evidence to inform the qualitative review"
            : "Snapshot history for this contract"
      }
      width="md"
    >
      <div data-testid="performance-grade-drawer-content">
        <div
          role="tablist"
          aria-label="Performance grade views"
          className="flex gap-4 border-b border-slate-200 px-6 dark:border-slate-700"
          onKeyDown={onTabsKeyDown}
        >
          <TabButton
            ref={historyRef}
            active={activeTab === "history"}
            panelId={PANEL_HISTORY}
            onClick={() => setActiveTab("history")}
            data-testid="performance-drawer-tab-history"
          >
            History
          </TabButton>
          <TabButton
            ref={howRef}
            active={activeTab === "how"}
            panelId={PANEL_HOW}
            onClick={() => setActiveTab("how")}
            data-testid="performance-drawer-tab-how"
          >
            How is this calculated?
          </TabButton>
          <TabButton
            ref={evidenceRef}
            active={activeTab === "issue-evidence"}
            panelId={PANEL_EVIDENCE}
            onClick={() => setActiveTab("issue-evidence")}
            data-testid="performance-drawer-tab-issue-evidence"
          >
            Issue evidence
          </TabButton>
        </div>

        {activeTab === "how" && (
          <div
            id={PANEL_HOW}
            role="tabpanel"
            aria-labelledby={`${PANEL_HOW}-tab`}
          >
            <HowCalculatedPanel
              result={result}
              pillars={pillars}
              watchThreshold={watchThreshold}
              successThreshold={successThreshold}
            />
          </div>
        )}
        {activeTab === "history" && (
          <div
            id={PANEL_HISTORY}
            role="tabpanel"
            aria-labelledby={`${PANEL_HISTORY}-tab`}
          >
            <HistoryPanel
              contractId={contractId}
              gradeThresholdOffset={gradeThresholdOffset}
            />
          </div>
        )}
        {activeTab === "issue-evidence" && (
          <div
            id={PANEL_EVIDENCE}
            role="tabpanel"
            aria-labelledby={`${PANEL_EVIDENCE}-tab`}
          >
            <IssueEvidencePanel contractId={contractId} />
          </div>
        )}
      </div>
    </SlideOverDrawer>
  );
}

function HowCalculatedPanel(props: {
  result: GradeResult;
  pillars: PillarSummary[];
  watchThreshold: number;
  successThreshold: number;
}): JSX.Element {
  const { result, pillars, watchThreshold, successThreshold } = props;
  const reportingPillars = pillars.filter((p) => p.score !== null);
  const reportingNote =
    reportingPillars.length === pillars.length
      ? null
      : `${reportingPillars.length} of ${pillars.length} pillars reporting — pillars without enough data are excluded from the average.`;

  return (
    <div className="space-y-6 p-6">
      <section className="space-y-2">
        <h3 className="text-h4 text-slate-900 dark:text-slate-50">
          This contract
        </h3>
        <dl className="space-y-2 text-body-sm">
          <div className="flex items-baseline justify-between">
            <dt className="text-slate-500 dark:text-slate-400">Grade</dt>
            <dd className="font-semibold text-slate-900 dark:text-slate-50">
              {result.grade ?? "—"}
              {result.label ? (
                <span className="ml-2 text-slate-500 dark:text-slate-400">
                  ({result.label})
                </span>
              ) : null}
            </dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-slate-500 dark:text-slate-400">Score</dt>
            <dd className="font-semibold text-slate-900 dark:text-slate-50">
              {result.score === null ? "—" : `${result.score} of 100`}
            </dd>
          </div>
          {result.capReason && (
            <div
              className="rounded-md bg-amber-50 p-2 text-caption text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
              data-testid="performance-grade-drawer-cap-reason"
            >
              Floor rule applied: {result.capReason}.
            </div>
          )}
        </dl>
      </section>

      <section className="space-y-2">
        <h3 className="text-h4 text-slate-900 dark:text-slate-50">
          Pillar contributions
        </h3>
        {reportingNote && (
          <p className="text-caption text-slate-500 dark:text-slate-400">
            {reportingNote}
          </p>
        )}
        <table className="w-full text-body-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-caption text-slate-500 dark:border-slate-700 dark:text-slate-400">
              <th className="py-2 font-medium">Pillar</th>
              <th className="py-2 text-right font-medium">Score</th>
              <th className="py-2 text-right font-medium">Sample</th>
              <th className="py-2 text-right font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {pillars.map((p) => (
              <tr
                key={p.label}
                className="border-b border-slate-100 last:border-b-0 dark:border-slate-800"
                data-testid={`performance-grade-drawer-pillar-${p.label.toLowerCase()}`}
              >
                <td className="py-2 text-slate-700 dark:text-slate-200">
                  {p.label}
                </td>
                <td className="py-2 text-right text-slate-900 dark:text-slate-50">
                  {formatPillarValue(p)}
                </td>
                <td className="py-2 text-right text-slate-500 dark:text-slate-400">
                  n={p.sampleSize}
                </td>
                <td className="py-2 text-right text-slate-700 dark:text-slate-200">
                  {pillarStatus(p, watchThreshold)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="space-y-2">
        <h3 className="text-h4 text-slate-900 dark:text-slate-50">
          How the grade is calculated
        </h3>
        <ol className="list-decimal space-y-2 pl-5 text-body-sm text-slate-700 dark:text-slate-200">
          <li>
            Each pillar is scored 0–100. A pillar with fewer than 2 samples is
            excluded from the average.
          </li>
          <li>
            The score is the equal-weighted average of the pillars that are
            reporting. Pillars without data don't reduce the score — they
            simply don't count.
          </li>
          <li>
            The score maps to a letter grade using these bands:
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-caption text-slate-500 dark:text-slate-400">
              <li>A — Excellent (≥ 90)</li>
              <li>B — Good (≥ 80)</li>
              <li>C — Monitor (≥ 70)</li>
              <li>D — Needs improvement (≥ 60)</li>
              <li>E — Critical (&lt; 60)</li>
            </ul>
          </li>
          <li>
            Floor rule: any pillar below the watch threshold ({watchThreshold}%)
            counts as critical. 1 critical caps the grade at C, 2 caps at D, 3
            caps at E. The final grade is the worst of the average and the cap.
          </li>
          <li>
            Per-pillar dial colours use the success threshold ({successThreshold}
            %) and the watch threshold ({watchThreshold}%). A pillar at or
            above success is green; between watch and success is amber; below
            watch is red.
          </li>
        </ol>
      </section>
    </div>
  );
}

function HistoryPanel(props: {
  contractId: string;
  gradeThresholdOffset: number | null;
}): JSX.Element {
  return (
    <div className="space-y-3 p-6">
      <h3 className="text-h4 text-slate-900 dark:text-slate-50">
        Performance history
      </h3>
      <p className="text-caption text-slate-500 dark:text-slate-400">
        A new snapshot is recorded whenever a deliverable, milestone, or KPI
        report changes the grade, or when threshold configuration is saved.
      </p>
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="md" />
          </div>
        }
      >
        <PerformanceHistoryChart
          contractId={props.contractId}
          currentGradeThresholdOffset={props.gradeThresholdOffset}
          enabled
        />
      </Suspense>
    </div>
  );
}
